import type { EntityName, OutboxOp, SyncColumns, SyncOperation } from '@cadencia/contracts';
import type { CadenciaDb } from './db.js';
import { newId, nowIso } from './ids.js';

/**
 * Escritura local + encolado en el outbox EN LA MISMA TRANSACCION.
 * Es la propiedad que hace que "guardar" nunca dependa de la red y que ningun
 * cambio se pierda si la app se cierra (docs/SYNC_ENGINE.md §3).
 */
export interface WriteContext {
  db: CadenciaDb;
  userId: string;
  deviceId: string;
}

export type LocalTable =
  | 'profiles'
  | 'userPreferences'
  | 'gyms'
  | 'equipment'
  | 'availableLoads'
  | 'trainingBlocks'
  | 'trainingWeeks'
  | 'trainingSessions'
  | 'sessionExercises'
  | 'setPrescriptions'
  | 'warmupPrescriptions'
  | 'readinessCheckins'
  | 'workoutLogs'
  | 'performedSets'
  | 'restEvents'
  | 'loadRecommendations'
  | 'progressionState'
  | 'trainingDecisions'
  | 'cardioPrescriptions'
  | 'cardioLogs'
  | 'nutritionTargets'
  | 'mealPlans'
  | 'plannedMeals'
  | 'mealLogs'
  | 'dailyWeights'
  | 'userRecipeSettings'
  | 'pantryItems'
  | 'shoppingLists'
  | 'shoppingListItems'
  | 'bodyMeasurements'
  | 'weeklyCheckins'
  | 'dailyActivity'
  // Evidencia (§8). Las dos las escribe el CLIENTE: la Edge Function busca y
  // devuelve, pero quien decide que se guarda —y como candidato— es el dominio
  // en el dispositivo, igual que con el resto de propuestas de IA.
  | 'evidenceSearches'
  | 'evidenceSources'
  // De las tres tablas de IA solo esta se escribe desde el cliente. La
  // migracion 0020 le dejo `insert`/`update` a `authenticated`; a
  // `ai_requests` y `ai_usage_budget` les quito todo menos `select`, porque su
  // contabilidad la lleva el servidor y un cliente que pudiera escribirla
  // podria tambien no escribirla.
  | 'aiDecisions';

const TABLE_TO_ENTITY: Record<LocalTable, EntityName> = {
  profiles: 'profiles',
  userPreferences: 'user_preferences',
  gyms: 'gyms',
  equipment: 'equipment',
  availableLoads: 'available_loads',
  trainingBlocks: 'training_blocks',
  trainingWeeks: 'training_weeks',
  trainingSessions: 'training_sessions',
  sessionExercises: 'session_exercises',
  setPrescriptions: 'set_prescriptions',
  warmupPrescriptions: 'warmup_prescriptions',
  readinessCheckins: 'readiness_checkins',
  workoutLogs: 'workout_logs',
  performedSets: 'performed_sets',
  restEvents: 'rest_events',
  loadRecommendations: 'load_recommendations',
  progressionState: 'progression_state',
  cardioPrescriptions: 'cardio_prescriptions',
  cardioLogs: 'cardio_logs',
  trainingDecisions: 'training_decisions',
  nutritionTargets: 'nutrition_targets',
  mealPlans: 'meal_plans',
  plannedMeals: 'planned_meals',
  mealLogs: 'meal_logs',
  dailyWeights: 'daily_weights',
  userRecipeSettings: 'user_recipe_settings',
  pantryItems: 'pantry_items',
  shoppingLists: 'shopping_lists',
  shoppingListItems: 'shopping_list_items',
  bodyMeasurements: 'body_measurements',
  weeklyCheckins: 'weekly_checkins',
  dailyActivity: 'daily_activity',
  evidenceSearches: 'evidence_searches',
  evidenceSources: 'evidence_sources',
  aiDecisions: 'ai_decisions',
};

/**
 * Columnas de contabilidad de la sincronizacion.
 *
 * `deletedAt` **no** esta aqui, y no es un descuido: la lapida es estado de
 * negocio, no contabilidad. Estuvo en esta lista hasta el 30/08/2026, y como
 * `sameBusinessFields` ignora todo lo que aparece aqui, un borrado suave —que
 * por definicion no cambia ningun otro campo— se tomaba por «guardar dos veces
 * lo mismo»: `putRow` devolvia la fila intacta, no escribia la lapida y no
 * encolaba nada. Se comio en silencio `unlogMeal` y el archivado de comidas de
 * `savePlanWithMeals`, asi que rehacer el menu de una semana dejaba dentro las
 * comidas viejas.
 */
const SYNC_KEYS = new Set([
  'id',
  'userId',
  'createdAt',
  'updatedAt',
  'rev',
  'originDeviceId',
  'syncSeq',
]);

/** Compara solo los campos de negocio, ignorando las columnas de sincronizacion. */
function sameBusinessFields(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)].filter((key) => !SYNC_KEYS.has(key)));
  for (const key of keys) {
    if (JSON.stringify(a[key]) !== JSON.stringify(b[key])) return false;
  }
  return true;
}

export function newSyncColumns(ctx: WriteContext, id = newId()): SyncColumns {
  const at = nowIso();
  return {
    id,
    userId: ctx.userId,
    createdAt: at,
    updatedAt: at,
    deletedAt: null,
    rev: 1,
    originDeviceId: ctx.deviceId,
    syncSeq: null,
  };
}

function makeOp(
  entity: EntityName,
  operation: SyncOperation,
  row: SyncColumns,
  payload: unknown,
): OutboxOp {
  return {
    opId: newId(),
    entity,
    entityId: row.id,
    operation,
    payload,
    baseRev: row.rev - 1,
    createdAt: nowIso(),
    attempts: 0,
    lastError: null,
    status: 'pending',
  };
}

/** Inserta o actualiza una fila y encola su operacion. */
export async function putRow<T extends SyncColumns>(
  ctx: WriteContext,
  table: LocalTable,
  row: T,
): Promise<T> {
  const entity = TABLE_TO_ENTITY[table];
  const store = ctx.db.table(table);

  return ctx.db.transaction('rw', [store, ctx.db.outbox], async () => {
    const existing = (await store.get(row.id)) as T | undefined;

    // Guardar dos veces lo mismo no es un cambio: no incrementa `rev` ni encola
    // nada. Evita que una doble pulsacion infle el contador de revision.
    if (existing && sameBusinessFields(existing, row)) return existing;

    const next: T = existing
      ? {
          ...row,
          createdAt: existing.createdAt,
          rev: existing.rev + 1,
          updatedAt: nowIso(),
          originDeviceId: ctx.deviceId,
        }
      : row;

    await store.put(next);
    await ctx.db.outbox.put(makeOp(entity, existing ? 'update' : 'insert', next, next));
    return next;
  });
}

/**
 * Inserta muchas filas nuevas en UNA sola transaccion (p. ej. al materializar
 * una semana de entrenamiento). Todas se encolan; si algo falla, no entra nada.
 */
export async function putNewRows<T extends SyncColumns>(
  ctx: WriteContext,
  entries: readonly { table: LocalTable; row: T }[],
): Promise<void> {
  if (entries.length === 0) return;
  const tables = [...new Set(entries.map((entry) => entry.table))].map((name) =>
    ctx.db.table(name),
  );

  await ctx.db.transaction('rw', [...tables, ctx.db.outbox], async () => {
    for (const entry of entries) {
      await ctx.db.table(entry.table).put(entry.row);
      await ctx.db.outbox.put(makeOp(TABLE_TO_ENTITY[entry.table], 'insert', entry.row, entry.row));
    }
  });
}

/**
 * Escribe un lote importado: TODO en una sola transaccion o nada.
 *
 * Existe aparte de `putNewRows` por dos motivos que no se pueden mezclar:
 *
 * 1. **La atomicidad es del §8.2**, no una comodidad. «O entra todo el lote
 *    validado o no entra nada»: un import a medias deja la base con hijos sin
 *    padre y sin nadie que sepa donde se corto. `putNewRows` ya es atomico,
 *    pero solo sabe insertar.
 * 2. **Aqui hay filas que YA existen.** Si la fila esta, la operacion es
 *    `update` y `rev` sube sobre lo que hubiera; si no esta, es `insert` y se
 *    respeta el `rev` que traiga el fichero. Encolar un `insert` sobre algo
 *    existente le llega al servidor como conflicto de clave.
 *
 * Las columnas de sincronizacion se reescriben a proposito: `userId` pasa a ser
 * el de quien importa —sin eso, RLS rechaza el lote entero al subirlo, que es
 * lo que haria inutil restaurar en una cuenta nueva—, `originDeviceId` pasa a
 * ser este dispositivo porque es el que va a empujar los cambios, y `syncSeq`
 * vuelve a `null` porque todavia no ha pasado por el servidor.
 */
export async function importRows(
  ctx: WriteContext,
  entries: readonly { table: LocalTable; row: SyncColumns }[],
): Promise<{ insertadas: number; actualizadas: number }> {
  if (entries.length === 0) return { insertadas: 0, actualizadas: 0 };

  const tables = [...new Set(entries.map((entry) => entry.table))].map((name) =>
    ctx.db.table(name),
  );

  return ctx.db.transaction('rw', [...tables, ctx.db.outbox], async () => {
    let insertadas = 0;
    let actualizadas = 0;

    for (const entry of entries) {
      const store = ctx.db.table(entry.table);
      const existing = (await store.get(entry.row.id)) as SyncColumns | undefined;

      const next: SyncColumns = {
        ...entry.row,
        userId: ctx.userId,
        originDeviceId: ctx.deviceId,
        syncSeq: null,
        ...(existing === undefined ? {} : { createdAt: existing.createdAt, rev: existing.rev + 1 }),
      };

      await store.put(next);
      await ctx.db.outbox.put(
        makeOp(TABLE_TO_ENTITY[entry.table], existing ? 'update' : 'insert', next, next),
      );
      if (existing) actualizadas += 1;
      else insertadas += 1;
    }

    return { insertadas, actualizadas };
  });
}

/** Borrado logico (tombstone) + operacion de borrado. */
export async function softDeleteRow(
  ctx: WriteContext,
  table: LocalTable,
  id: string,
): Promise<void> {
  const entity = TABLE_TO_ENTITY[table];
  const store = ctx.db.table(table);

  await ctx.db.transaction('rw', [store, ctx.db.outbox], async () => {
    const existing = (await store.get(id)) as SyncColumns | undefined;
    if (!existing || existing.deletedAt !== null) return;
    const at = nowIso();
    const next: SyncColumns = {
      ...existing,
      deletedAt: at,
      updatedAt: at,
      rev: existing.rev + 1,
      originDeviceId: ctx.deviceId,
    };
    await store.put(next);
    await ctx.db.outbox.put(makeOp(entity, 'delete', next, next));
  });
}

/**
 * Reemplaza en bloque las filas derivadas de una entidad (p. ej. las cargas
 * disponibles de una maquina). Al ser derivadas, se recalculan en vez de
 * fusionarse (docs/SYNC_ENGINE.md §5.4).
 */
export async function replaceDerivedRows<T extends SyncColumns>(
  ctx: WriteContext,
  table: LocalTable,
  index: string,
  indexValue: string,
  rows: T[],
): Promise<void> {
  const entity = TABLE_TO_ENTITY[table];
  const store = ctx.db.table(table);

  await ctx.db.transaction('rw', [store, ctx.db.outbox], async () => {
    const previous = (await store.where(index).equals(indexValue).toArray()) as SyncColumns[];
    for (const old of previous) {
      if (old.deletedAt !== null) continue;
      const at = nowIso();
      const tombstone: SyncColumns = {
        ...old,
        deletedAt: at,
        updatedAt: at,
        rev: old.rev + 1,
        originDeviceId: ctx.deviceId,
      };
      await store.put(tombstone);
      await ctx.db.outbox.put(makeOp(entity, 'delete', tombstone, tombstone));
    }
    for (const row of rows) {
      await store.put(row);
      await ctx.db.outbox.put(makeOp(entity, 'insert', row, row));
    }
  });
}
